import React, { PureComponent } from 'react';
import { querySelectors, requestAsync } from 'redux-query';
import { connect } from 'react-redux';
import { DateTime } from 'luxon';
import GoogleMapReact from 'google-map-react';
import { Spinner } from '@el8/vital';
import { MapPointer, Box } from 'components';
import { RouteComponentProps } from '@reach/router';
import { getAppointmentTypeForUrl } from 'modules/appointmentType';
import { getNextAvailabilitiesQuery } from 'modules/availability';
import { getBookingInfo, updateBookingInfo } from 'modules/booking';
import {
  getPractice,
  fetchPracticeLocation as fetchPracticeLocationAction,
} from 'modules/practice';
import { getPhysiciansForAppointmentUrl, getBookablePhysicians } from 'modules/physician';
import {
  PracticeInfo,
  State,
  Dispatch,
  PhysicianInfo,
  RQGenericResponse,
  AppointmentTypesState,
  ServiceLocationInfo,
} from 'types';
import { AppointmentType } from 'EntityTypes';

import { setStartTime, logEvent } from 'utils/amplitude';
import { fetchServiceLocationCoordinates, getServiceLocations } from 'modules/serviceLocations';
import PracticeHeader from './PracticeHeader';
import ServiceLocations from './ServiceLocations';
import Service from '../Service/Service';
import styles from './Practice.less';

type OwnProps = {
  practiceId: number;
} & RouteComponentProps<{ appointmentTypeUrl?: string }>;

type StateProps = {
  appointmentTypeFromUrl: AppointmentType;
  appointmentTypeIdWithOpenCalendar: number;
  appointmentTypes: AppointmentTypesState;
  practice: PracticeInfo;
  physicians: PhysicianInfo[];
  hasAvailabilitiesForSelectedAppointmentType: boolean;
  isLoadingFinished: boolean;
  selectedPhysicianId: number;
  selectedTimezone: string;
  serviceLocations: ServiceLocationInfo[];
};

type DispatchProps = {
  fetchNextAvailabilities: (appointmentTypeIds?: string[]) => void;
  fetchPracticeLocation: (practiceInfo: PracticeInfo) => void;
  fetchServiceLocationCoords: (serviceLocations: ServiceLocationInfo[]) => void;
  updateBookingTimezone: (timezone: string) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const mapStateToProps = (
  state: State,
  { practiceId, appointmentTypeUrl }: OwnProps,
): StateProps => {
  const bookingInfo = getBookingInfo(state);
  const selectedDate = bookingInfo.scheduledDate
    ? DateTime.fromISO(bookingInfo.scheduledDate).toISODate()
    : undefined;

  return {
    appointmentTypes: state.entities.appointmentTypes,
    appointmentTypeFromUrl: getAppointmentTypeForUrl(state, { appointmentTypeUrl }),
    appointmentTypeIdWithOpenCalendar: bookingInfo.calendar.activeAppointmentTypeId,
    hasAvailabilitiesForSelectedAppointmentType:
      !!state.entities.availabilities?.[bookingInfo.calendar.activeAppointmentTypeId]?.[
        selectedDate
      ],
    isLoadingFinished: querySelectors.isFinished(
      state.queries,
      getNextAvailabilitiesQuery(practiceId),
    ),
    physicians: appointmentTypeUrl
      ? getPhysiciansForAppointmentUrl(state, { appointmentTypeUrl })
      : getBookablePhysicians(state),
    practice: getPractice(state, { practiceId }),
    selectedPhysicianId: bookingInfo.selectedPhysicianId,
    selectedTimezone: bookingInfo.timezone,
    serviceLocations: getServiceLocations(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch, props: OwnProps): DispatchProps => ({
  fetchNextAvailabilities: (appointmentTypeIds?: string[]): Promise<RQGenericResponse> =>
    dispatch(
      requestAsync(
        getNextAvailabilitiesQuery(props.practiceId, { appointmentTypeIds, force: true }),
      ),
    ),
  fetchPracticeLocation: (practiceInfo): Promise<RQGenericResponse> =>
    dispatch(fetchPracticeLocationAction(practiceInfo)),
  fetchServiceLocationCoords: (serviceLocations): void => {
    serviceLocations.forEach((loc) => dispatch(fetchServiceLocationCoordinates(loc)));
  },
  updateBookingTimezone: (timezone: string): void => {
    dispatch(updateBookingInfo({ timezone }));
  },
});

const getMapOptions = (): { disableDefaultUI: boolean; zoomControl: boolean } => ({
  disableDefaultUI: true,
  zoomControl: true,
});

// Return map bounds based on list of places
const getMapBounds = (places: ServiceLocationInfo[]): google.maps.LatLngBounds => {
  const bounds = new google.maps.LatLngBounds();
  places.forEach((place) => {
    if (place.lat && place.lng) bounds.extend(new google.maps.LatLng(place.lat, place.lng));
  });
  return bounds;
};

// Fit map to its bounds after the api is loaded
// pulled from this example https://github.com/google-map-react/google-map-react-examples/blob/228f0dcd86183989699457ce1e4d085a831c3002/src/examples/Main.js#L36
const apiIsLoaded = (map: google.maps.Map, places: ServiceLocationInfo[]): void => {
  // Get bounds by our places
  const bounds = getMapBounds(places);
  // Fit map to bounds
  map.fitBounds(bounds);
};

class Practice extends PureComponent<Props> {
  localTimezone: string;

  constructor(props: Props) {
    super(props);
    this.localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  componentDidMount(): void {
    const {
      appointmentTypes,
      appointmentTypeFromUrl,
      fetchNextAvailabilities,
      fetchPracticeLocation,
      fetchServiceLocationCoords,
      practice,
      practiceId,
      hasAvailabilitiesForSelectedAppointmentType,
      appointmentTypeIdWithOpenCalendar,
      updateBookingTimezone,
      serviceLocations,
    } = this.props;
    const { name, timezone } = practice;
    setStartTime();
    logEvent('booking started', {
      'practice id': practiceId,
      'practice name': name,
    });
    if (this.localTimezone !== timezone) {
      logEvent('show timezone', {
        'practice timezone': timezone,
        'user timezone': this.localTimezone,
      });
    }

    // International timezones not supported yet. Update timezone to practice timezone if
    // current timezone is outside the US
    const isInternationalTimezone = Intl.DateTimeFormat().resolvedOptions().locale !== 'en-US';
    if (isInternationalTimezone) {
      updateBookingTimezone(practice.timezone);
    }

    fetchPracticeLocation(practice);

    if (appointmentTypeFromUrl) {
      if (!hasAvailabilitiesForSelectedAppointmentType) {
        fetchNextAvailabilities([String(appointmentTypeFromUrl.id)]);
      }
    } else if (!window.el8Globals.FEATURES.AvailabilityOnBkSite) {
      // When the AvailabilityOnBkSite feature flag is off...
      // If the user had an open calendar for one of the services and then coming
      // back to this page, we want to restore the state of the page (open the
      // calendar on the same month).
      // To do that, we need to load the next availabilities for all services except
      // the one that has calendar open because next availabilities include only 7
      // nearest time slots, and for the open calendar, we need availabilities for
      // the whole month.
      // We will fetch availabilities for the calendar in the <Calendar /> component
      // in the background.
      fetchNextAvailabilities(
        hasAvailabilitiesForSelectedAppointmentType
          ? Object.keys(appointmentTypes).filter(
              (id): boolean => id !== String(appointmentTypeIdWithOpenCalendar),
            )
          : undefined,
      );
    }

    if (serviceLocations) fetchServiceLocationCoords(serviceLocations);
  }

  renderServices = (): JSX.Element => {
    const {
      navigate,
      isLoadingFinished,
      hasAvailabilitiesForSelectedAppointmentType,
      appointmentTypeFromUrl,
      selectedPhysicianId,
      practice,
      practiceId,
    } = this.props;

    if (selectedPhysicianId === undefined && !appointmentTypeFromUrl) return null;

    if (!isLoadingFinished && !hasAvailabilitiesForSelectedAppointmentType) {
      return (
        <Box className={styles.loaderContainer} align="center" justify="center">
          <Spinner />
        </Box>
      );
    }

    if (appointmentTypeFromUrl) {
      return (
        <Service
          appointmentTypeId={appointmentTypeFromUrl.id}
          physicianId={selectedPhysicianId}
          practiceId={practiceId}
          navigate={navigate}
        />
      );
    }

    if (practice.appointmentTypes && !!practice.appointmentTypes.length) {
      return (
        <div className={styles.services}>
          {practice.appointmentTypes.map(
            (appointmentTypeId): JSX.Element => (
              <Service
                key={appointmentTypeId}
                appointmentTypeId={appointmentTypeId}
                physicianId={selectedPhysicianId}
                practiceId={practiceId}
                navigate={navigate}
              />
            ),
          )}
        </div>
      );
    }
    return null;
  };

  render(): JSX.Element {
    const { navigate, practice, practiceId, serviceLocations } = this.props;
    const { lat, lng } = practice;

    const isAvailabilityOnBkSiteEnabled = window.el8Globals.FEATURES.AvailabilityOnBkSite;
    const isSchedulingTimeZoneEnabled = window.el8Globals.FEATURES.SchedulingTimeZone;

    return (
      <Box align="top" className={styles.practice}>
        <div className={styles.mainInfo}>
          <PracticeHeader practiceId={practiceId} />
          {isAvailabilityOnBkSiteEnabled ? (
            <ServiceLocations navigate={navigate} practiceId={practiceId} />
          ) : (
            this.renderServices()
          )}
        </div>
        {!!(lat && lng) && (
          <div className={styles.map}>
            {isAvailabilityOnBkSiteEnabled && isSchedulingTimeZoneEnabled ? (
              <GoogleMapReact
                bootstrapURLKeys={{ key: window.el8Globals.GOOGLE_MAPS_API_KEY }}
                defaultCenter={{ lat, lng }}
                defaultZoom={13}
                options={getMapOptions}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map }): void => apiIsLoaded(map, serviceLocations)}
              >
                {serviceLocations.map((loc) => (
                  <MapPointer lat={loc.lat} lng={loc.lng} key={loc.id} />
                ))}
              </GoogleMapReact>
            ) : (
              <GoogleMapReact
                bootstrapURLKeys={{ key: window.el8Globals.GOOGLE_MAPS_API_KEY }}
                defaultCenter={{ lat, lng }}
                defaultZoom={13}
                options={getMapOptions}
              >
                <MapPointer lat={lat} lng={lng} />
              </GoogleMapReact>
            )}
          </div>
        )}
      </Box>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Practice);
